import { ChevronDown, ChevronRight } from "lucide-react";
import SettingRow from "@/components/admin/SettingRow";

/**
 * A setting group inside a category, and the whole of the complexity-tier UI.
 *
 * Bible §20: the tiers are an internal progressive-disclosure mechanism, not a
 * difficulty selector. So there is no Simple | Advanced | Expert control anywhere
 * on this screen. Normal settings are simply there; advanced ones sit behind
 * "Advanced options"; hardware and infrastructure sit behind "Expert / Hardware"
 * and are collapsed until asked for. Raw keys only ever show inside the expert
 * band, which is the expert view §18 reserves them for.
 *
 * Rows read their value from the page's staged drafts (`valueOf`) — nothing here
 * saves; the page's save bar owns the one commit.
 */
export default function SettingBand({ band, open, onToggle, ...rowProps }) {
  const rows = band.items.map(meta => {
    if (!rowProps.resolved?.[meta.key]) return null;
    return (
      <SettingRow
        key={meta.key}
        meta={meta}
        value={rowProps.valueOf(meta.key)}
        dirty={rowProps.dirtyKeys.includes(meta.key)}
        allSettings={rowProps.resolved}
        onChange={rowProps.onDraft}
        readOnly={rowProps.readOnly}
        focused={rowProps.focusKey === meta.key}
        onTouched={rowProps.onTouched}
        showRawIds={band.id === "expert"}
        error={(rowProps.problems || []).find(problem => problem.field && problem.keys.includes(meta.key))?.message}
      />
    );
  });

  if (band.id === "normal") {
    return (
      <div className="rounded-md border bg-card overflow-hidden" data-testid="settings-band-normal">
        {rows}
      </div>
    );
  }

  const Twisty = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-md border bg-card overflow-hidden" data-testid={`settings-band-${band.id}`}>
      <button
        type="button"
        className="w-full flex items-center gap-1.5 px-3 min-h-8 text-meta font-medium text-muted-foreground hover:bg-muted transition-colors"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`settings-band-${band.id}-toggle`}
      >
        <Twisty className="w-3 h-3" aria-hidden="true" />
        <span>{band.label}</span>
        <span className="ml-auto text-micro tabular-nums opacity-70">{band.items.length}</span>
      </button>
      {open && <div className="border-t">{rows}</div>}
    </div>
  );
}
